import { AxiosError } from 'axios';
import type { Project } from '@shared/models/project.model';
import { ErrorMessage } from '@shared/constants/server-errors.constants';
import { guards } from '@shared/helpers/typeguards.helpers';
import type { ProjectChange } from '@shared/helpers/pipeline/update-engine.models';
import type { ProposalId } from '@shared/models/proposals.model';
import type { BuildProposalAppendActionPatchOptions } from '@shared/helpers/actions/apply-changes-to-proposal.helpers';
import { applyProjectChangesWithProposalContext } from '@shared/helpers/actions/apply-project-changes-with-proposal-context.helpers';
import { applyVersionMetadata } from '@shared/helpers/project.helpers';

/** User-facing copy shown when a save could not be replayed and the store was reset to server truth. */
export const RACE_RECOVERY_SNACKBAR_MESSAGE =
  'Your changes could not be saved. Loaded the latest version.';

/** Reads `{ error: string }` from an {@link AxiosError} response body, if present. */
const getAxiosErrorMessage = (error: AxiosError): string | undefined => {
  const body = error.response?.data;
  if (
    guards.is.object(body) &&
    'error' in body &&
    guards.is.string(body.error)
  ) {
    return body.error;
  }
  return undefined;
};

/** Inputs for {@link projectChangesReplayLands}. */
export type ProjectChangesReplay = {
  changes: ProjectChange[];
  selectedProposalId?: ProposalId;
  proposalAppend?: BuildProposalAppendActionPatchOptions;
  /** When set, a version switch counts as a landed replay even if `changes` are a no-op. */
  activeVersionId?: string;
};

/**
 * Whether re-applying {@link ProjectChangesReplay.changes} on fresh server data
 * actually changes that project. A missing target (deleted upstream) leaves the
 * same reference, so recovery must reset and tell the user instead of saving a no-op.
 * An {@link ProjectChangesReplay.activeVersionId} counts only when
 * {@link applyVersionMetadata} changes the project — an unknown id falls back to
 * the current version and must not look like a landed replay.
 *
 * Runs as a race replay: element removes already absent on `fresh` are the
 * winning write and are not reported. A direct edit still reports that miss.
 */
export const projectChangesReplayLands = (
  fresh: Project,
  replay: ProjectChangesReplay,
): boolean => {
  if (replay.changes.length === 0) return false;
  const changed = applyProjectChangesWithProposalContext(
    fresh,
    replay.selectedProposalId,
    replay.changes,
    replay.proposalAppend,
    { replayOfRace: true },
  );
  // Same check the replay persist uses. An id that is not on the project falls
  // back to the current version and keeps this reference.
  const withVersion =
    replay.activeVersionId !== undefined
      ? applyVersionMetadata(changed, replay.activeVersionId)
      : changed;
  return withVersion !== fresh;
};

/**
 * Narrows an unknown error to the server optimistic-lock rejection
 * ({@link ErrorMessage.RaceCondition} → HTTP 422). Matches both the Axios
 * response (status/body) and a plain {@link Error} carrying the message.
 */
export const isRaceConditionError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    if (error.response?.status === 422) return true;
    return getAxiosErrorMessage(error) === ErrorMessage.RaceCondition;
  }
  if (error instanceof Error) {
    return error.message === ErrorMessage.RaceCondition;
  }
  return false;
};

/** Dependencies for {@link buildRaceRecoveryReset}. */
export type RaceRecoveryResetDeps = {
  /** Drain the PUT queue so no stale sibling requests keep cascading 422s. */
  clearQueue: () => void;
  /** Drop ghost undo/redo frames left by the discarded optimistic edit. */
  resetUndoRedoHistory: () => void;
  /** Abort any in-flight enrichment so it cannot re-commit the discarded state. */
  bumpEnrichmentCancellationSequence: () => void;
  /** Write the given project into the store as the new source of truth. */
  commitLocally: (project: Project) => Project;
  /** Inform the user that their edit could not be saved. */
  onSnackbar: (message: string) => void;
};

/**
 * Builds the fallback used when a save cannot be replayed: reset the store to
 * the freshly fetched server project and clean up all client-side write state.
 */
export const buildRaceRecoveryReset =
  (deps: RaceRecoveryResetDeps) =>
  (fresh: Project): Project => {
    deps.clearQueue();
    deps.resetUndoRedoHistory();
    deps.bumpEnrichmentCancellationSequence();
    deps.onSnackbar(RACE_RECOVERY_SNACKBAR_MESSAGE);
    return deps.commitLocally(fresh);
  };
